import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import DataTable, { type DataTableColumn } from "components/ui/DataTable/DataTable";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import Modal from "components/ui/Modal/Modal";
import { useI18n } from "context/I18nContext";
import { useAuth } from "context/AuthContext";
import { ApiError } from "utils/apiClient";
import {
  fetchDeviceLogs,
  fetchDeviceRequestTypes,
  type DeviceLogEntry,
  type DeviceLogsMeta,
  type DeviceRequestType,
} from "utils/deviceLogsApi";
import { formatDateTime, toDateInputValue } from "utils/dateUtils";
import { normalizeRoleName, RoleName } from "constants/roles";
import styles from "./DeviceLogsPage.module.scss";

const PAGE_SIZE = 25;

const buildRequestTypeLabel = (entry: DeviceLogEntry | null) => {
  if (!entry) {
    return "";
  }
  if (entry.requestTypeName?.trim()) {
    return entry.requestTypeName.trim();
  }
  if (entry.requestTypeId) {
    return `#${entry.requestTypeId}`;
  }
  return "";
};

const stringifyPayload = (payload: unknown) => {
  if (payload === null || payload === undefined) {
    return "";
  }
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return payload;
    }
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
};

function DeviceLogsPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const normalizedRoles = useMemo(
    () =>
      (user?.roles ?? [])
        .map((role) => normalizeRoleName(role.roleName))
        .filter((roleName): roleName is RoleName => Boolean(roleName)),
    [user]
  );
  const isSuperAdmin = normalizedRoles.includes(RoleName.SUPER_ADMIN);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Math.max(1, Number(searchParams.get("page")) || 1);
  const deviceIdParam = searchParams.get("deviceId") ?? "";
  const cardNumberParam = searchParams.get("cardNumber") ?? "";
  const requestTypeParam = searchParams.get("requestTypeId") ?? "";
  const fromDateParam = searchParams.get("fromDate") ?? "";
  const toDateParam = searchParams.get("toDate") ?? "";

  const [deviceIdInput, setDeviceIdInput] = useState(deviceIdParam);
  const [cardNumberInput, setCardNumberInput] = useState(cardNumberParam);
  const [requestTypeInput, setRequestTypeInput] = useState(requestTypeParam);
  const [fromDateInput, setFromDateInput] = useState(() => toDateInputValue(fromDateParam));
  const [toDateInput, setToDateInput] = useState(() => toDateInputValue(toDateParam));

  useEffect(() => {
    setDeviceIdInput(deviceIdParam);
  }, [deviceIdParam]);

  useEffect(() => {
    setCardNumberInput(cardNumberParam);
  }, [cardNumberParam]);

  useEffect(() => {
    setRequestTypeInput(requestTypeParam);
  }, [requestTypeParam]);

  useEffect(() => {
    setFromDateInput(toDateInputValue(fromDateParam));
  }, [fromDateParam]);

  useEffect(() => {
    setToDateInput(toDateInputValue(toDateParam));
  }, [toDateParam]);

  const [logs, setLogs] = useState<DeviceLogEntry[]>([]);
  const [meta, setMeta] = useState<DeviceLogsMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestTypes, setRequestTypes] = useState<DeviceRequestType[]>([]);
  const [requestTypeError, setRequestTypeError] = useState<string | null>(null);
  const [requestTypesLoading, setRequestTypesLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<DeviceLogEntry | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchDeviceLogs({
          page: pageParam,
          pageSize: PAGE_SIZE,
          deviceId: deviceIdParam || undefined,
          cardNumber: cardNumberParam || undefined,
          requestTypeId: requestTypeParam || undefined,
          fromDate: fromDateParam || undefined,
          toDate: toDateParam || undefined,
          signal: controller.signal,
        });
        setLogs(response.items);
        setMeta(response.meta);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError?.message || t("deviceLogsPage.error"));
        setLogs([]);
        setMeta(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [
    cardNumberParam,
    deviceIdParam,
    fromDateParam,
    isSuperAdmin,
    pageParam,
    requestTypeParam,
    t,
    toDateParam,
  ]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setRequestTypes([]);
      return;
    }
    const controller = new AbortController();
    const loadRequestTypes = async () => {
      setRequestTypesLoading(true);
      setRequestTypeError(null);
      try {
        const response = await fetchDeviceRequestTypes(controller.signal);
        setRequestTypes(response);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        const apiError = err instanceof ApiError ? err : null;
        setRequestTypeError(apiError?.message || t("deviceLogsPage.requestTypesError"));
      } finally {
        setRequestTypesLoading(false);
      }
    };
    loadRequestTypes();
    return () => controller.abort();
  }, [isSuperAdmin, t]);

  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const trimmedDeviceId = deviceIdInput.trim();
    const trimmedCard = cardNumberInput.trim();
    if (trimmedDeviceId) {
      next.set("deviceId", trimmedDeviceId);
    } else {
      next.delete("deviceId");
    }
    if (trimmedCard) {
      next.set("cardNumber", trimmedCard);
    } else {
      next.delete("cardNumber");
    }
    if (requestTypeInput) {
      next.set("requestTypeId", requestTypeInput);
    } else {
      next.delete("requestTypeId");
    }
    if (fromDateInput) {
      next.set("fromDate", fromDateInput);
    } else {
      next.delete("fromDate");
    }
    if (toDateInput) {
      next.set("toDate", toDateInput);
    } else {
      next.delete("toDate");
    }
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  const handleResetFilters = () => {
    setDeviceIdInput("");
    setCardNumberInput("");
    setRequestTypeInput("");
    setFromDateInput("");
    setToDateInput("");
    const next = new URLSearchParams();
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage === pageParam) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, nextPage)));
    setSearchParams(next, { replace: false });
  };

  const columns = useMemo<DataTableColumn<DeviceLogEntry>[]>(
    () => [
      {
        id: "requestAt",
        header: t("deviceLogsPage.table.requestAt"),
        accessorKey: "requestAt",
        cell: (value) => <span>{formatDateTime(value, language)}</span>,
      },
      {
        id: "requestType",
        header: t("deviceLogsPage.table.requestType"),
        cell: (_, row) => {
          const label = buildRequestTypeLabel(row);
          return (
            <span className={styles.requestTypePill} data-empty={label ? undefined : true}>
              {label || t("deviceLogsPage.table.unknownRequestType")}
            </span>
          );
        },
      },
      {
        id: "deviceId",
        header: t("deviceLogsPage.table.deviceId"),
        accessorKey: "deviceId",
      },
      {
        id: "cardNumber",
        header: t("deviceLogsPage.table.cardNumber"),
        accessorKey: "cardNumber",
      },
      {
        id: "message",
        header: t("deviceLogsPage.table.message"),
        accessorKey: "message",
        cell: (value) => {
          const resolved =
            typeof value === "string" && value.trim()
              ? value
              : t("deviceLogsPage.table.noMessage");
          return <span className={styles.messageCell}>{resolved}</span>;
        },
      },
      {
        id: "requestObject",
        header: t("deviceLogsPage.table.payload"),
        cell: (_, row) =>
          row.requestObject ? (
            <button
              type="button"
              className={styles.payloadButton}
              onClick={() => setSelectedLog(row)}
            >
              {t("deviceLogsPage.table.viewPayload")}
            </button>
          ) : (
            <span className={styles.emptyValue}>{t("deviceLogsPage.table.noPayload")}</span>
          ),
      },
    ],
    [language, t]
  );

  const payloadModalTitle = t("deviceLogsPage.payloadModal.title");
  const selectedTypeLabel = buildRequestTypeLabel(selectedLog);
  const modalTitle = selectedLog && selectedTypeLabel
    ? `${payloadModalTitle} · ${selectedTypeLabel}`
    : payloadModalTitle;

  const payloadText = stringifyPayload(selectedLog?.requestObject);

  if (!isSuperAdmin) {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.header}>
            <h1 className={styles.title}>{t("deviceLogsPage.accessDenied.title")}</h1>
            <p className={styles.subtitle}>{t("deviceLogsPage.accessDenied.message")}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("deviceLogsPage.title")}</h1>
          <p className={styles.subtitle}>{t("deviceLogsPage.subtitle")}</p>
        </div>

        <form className={styles.filters} onSubmit={handleFilterSubmit}>
          <div className={styles.filterGrid}>
            <label className={styles.filterField} htmlFor="device-log-device">
              <span className={styles.fieldLabel}>{t("deviceLogsPage.filters.deviceId")}</span>
              <input
                id="device-log-device"
                className={styles.input}
                type="text"
                value={deviceIdInput}
                onChange={(event) => setDeviceIdInput(event.target.value)}
                placeholder="ATM0000001"
                autoComplete="off"
              />
            </label>
            <label className={styles.filterField} htmlFor="device-log-card">
              <span className={styles.fieldLabel}>{t("deviceLogsPage.filters.cardNumber")}</span>
              <input
                id="device-log-card"
                className={styles.input}
                type="text"
                value={cardNumberInput}
                onChange={(event) => setCardNumberInput(event.target.value)}
                placeholder="5500************"
                autoComplete="off"
              />
            </label>
            <label className={styles.filterField} htmlFor="device-log-request-type">
              <span className={styles.fieldLabel}>{t("deviceLogsPage.filters.requestType")}</span>
              <select
                id="device-log-request-type"
                className={styles.select}
                value={requestTypeInput}
                onChange={(event) => setRequestTypeInput(event.target.value)}
              >
                <option value="">{t("deviceLogsPage.filters.allRequestTypes")}</option>
                {requestTypes.map((type) => (
                  <option key={type.id} value={String(type.id)}>
                    {type.name}
                  </option>
                ))}
              </select>
              {requestTypesLoading && (
                <span className={styles.fieldHint}>{t("deviceLogsPage.filters.loadingTypes")}</span>
              )}
              {requestTypeError && (
                <span className={styles.errorHint}>{requestTypeError}</span>
              )}
            </label>
            <label className={styles.filterField} htmlFor="device-log-from-date">
              <span className={styles.fieldLabel}>{t("deviceLogsPage.filters.fromDate")}</span>
              <input
                id="device-log-from-date"
                className={styles.input}
                type="date"
                value={fromDateInput}
                max={toDateInput || undefined}
                onChange={(event) => setFromDateInput(event.target.value)}
              />
            </label>
            <label className={styles.filterField} htmlFor="device-log-to-date">
              <span className={styles.fieldLabel}>{t("deviceLogsPage.filters.toDate")}</span>
              <input
                id="device-log-to-date"
                className={styles.input}
                type="date"
                value={toDateInput}
                min={fromDateInput || undefined}
                onChange={(event) => setToDateInput(event.target.value)}
              />
            </label>
          </div>
          <div className={styles.filterActions}>
            <button type="button" className={styles.secondaryButton} onClick={handleResetFilters}>
              {t("deviceLogsPage.filters.reset")}
            </button>
            <button type="submit" className={styles.primaryButton}>
              {t("deviceLogsPage.filters.submit")}
            </button>
          </div>
        </form>

        {error && (
          <div className={styles.alertStack}>
            <Alert variant="error" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        <DataTable
          columns={columns}
          data={logs}
          globalSearch={false}
          loading={loading}
          loadingText={t("deviceLogsPage.loading")}
          emptyState={loading ? t("deviceLogsPage.loading") : t("deviceLogsPage.empty")}
          manualPagination={
            meta
              ? {
                  pageIndex: Math.max((meta.page ?? 1) - 1, 0),
                  pageCount: Math.max(meta.pages ?? 1, 1),
                  pageSize: meta.pageSize ?? PAGE_SIZE,
                  totalItems: meta.total ?? logs.length,
                  onPageChange: (index) => handlePageChange(index + 1),
                }
              : undefined
          }
          showPaginationControls={false}
          showPaginationInfo={false}
        />

        {meta && (
          <PaginationFooter
            totalLabel={t("deviceLogsPage.meta.total")}
            pageLabel={t("deviceLogsPage.meta.page")}
            pageSizeLabel={t("deviceLogsPage.meta.pageSize")}
            total={meta.total}
            page={meta.page}
            pageCount={meta.pages}
            pageSize={meta.pageSize}
            previousLabel={t("datatable.previous")}
            nextLabel={t("datatable.next")}
            goToLabel={t("deviceLogsPage.pagination.goToLabel")}
            goButtonLabel={t("deviceLogsPage.pagination.goButton")}
            invalidMessage={(max) =>
              t("deviceLogsPage.pagination.invalidPage", {
                max,
              })
            }
            onPageChange={handlePageChange}
            className={styles.paginationFooter}
          />
        )}
      </Card>

      <Modal
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        title={modalTitle}
        description={t("deviceLogsPage.payloadModal.description")}
        size="lg"
      >
        {payloadText ? (
          <pre className={styles.jsonViewer}>{payloadText}</pre>
        ) : (
          <p className={styles.emptyValue}>{t("deviceLogsPage.table.noPayload")}</p>
        )}
      </Modal>
    </div>
  );
}

export default DeviceLogsPage;
